import {
  Panel,
  PanelAction,
  RoutedPanelProps,
} from '@/components/Layout/Panel/Panel';
import { Lightbox, type LightboxRef } from '@/components/ui/Lightbox';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveUrl } from '@/lib/api-utils';
import { cn } from '@/lib/utils';
import { CatalogContentFile } from '@certhon/models';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, ExternalLink, FileText } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { docsQueries } from './docs.queries';
import bytes from 'bytes';
import { findFile, getNodeLabel, revisionLabel } from './docs.tree';
import { useFilePreview } from './useFilePreview';
import { useState } from 'react';

/**
 * Terminal panel for a file. Route segment is `:groupOrFileId/file`; the
 * file id lives in `params.groupOrFileId`.
 *
 * Body shows revision metadata; header actions are Open (current tab),
 * New tab, Download — all hit /api/files/:id. Auth flows via cookie so a
 * plain <a> link works for downloads.
 *
 * PDF preview thumbnails are deferred to a follow-up.
 */
export const DocsDetailsPanel = ({
  params,
  parentParams,
  ...panelProps
}: RoutedPanelProps) => {
  const projectId = parseInt(parentParams.projectId ?? params.projectId);
  const fileIdRaw = params.groupOrFileId;
  const fileId =
    fileIdRaw === undefined || fileIdRaw === null ? null : parseInt(fileIdRaw);

  const { data: rows, isLoading } = useQuery(docsQueries.byProject(projectId));

  const file = rows && fileId !== null ? findFile(rows, fileId) : null;
  const hasRevision = !!file && file.file_recnum != null;

  // Match the tree's label resolution (en, then other langs, then #recnum)
  // so the panel title agrees with the row the user clicked.
  const titleLabel = file ? getNodeLabel(file, 'en') : `File ${fileId}`;

  // /files/:id wants catalog_filetoken.recnum (file.recnum), not the
  // revision row id (file.file_recnum). hasRevision still gates on
  // file_recnum so we only link when a current revision exists.
  const fileUrl = hasRevision ? resolveUrl(`/files/${file!.recnum}`) : null;
  const downloadFilename = file
    ? `${titleLabel}${file.file_extension ? '.' + file.file_extension : ''}`
    : undefined;

  const lightboxRef = useRef<LightboxRef>(null);
  const contentType =
    file?.mime ?? inferContentType(file?.file_extension ?? null);
  const lightboxItems = useMemo(() => {
    if (!fileUrl || !hasRevision) return [];
    return [
      {
        key: String(fileId),
        url: fileUrl,
        contentType,
        filename: downloadFilename,
      },
    ];
  }, [fileUrl, contentType, downloadFilename, hasRevision, fileId]);

  const openInViewer = () => {
    if (fileId !== null) lightboxRef.current?.open(String(fileId));
  };

  const actions: PanelAction[] = useMemo(() => {
    if (!fileUrl || !hasRevision) return [];
    return [
      {
        icon: ExternalLink,
        label: 'New tab',
        onCalled: () => {
          window.open(fileUrl, '_blank', 'noopener');
        },
      },
      {
        icon: Download,
        label: 'Download',
        onCalled: () => {
          const a = document.createElement('a');
          a.href = fileUrl;
          if (downloadFilename) a.download = downloadFilename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        },
      },
    ];
  }, [fileUrl, downloadFilename, hasRevision]);

  return (
    <Panel {...panelProps} title={titleLabel} icon={FileText} actions={actions}>
      {isLoading ? (
        <FileDetailsSkeleton />
      ) : !file ? (
        <p className="text-muted-foreground p-4 text-sm">File not found.</p>
      ) : (
        <FileDetailsBody file={file} onPreviewClick={openInViewer} />
      )}
      {lightboxItems.length > 0 && (
        <Lightbox ref={lightboxRef} items={lightboxItems} />
      )}
    </Panel>
  );
};

/**
 * Best-effort MIME guess from a file extension. The Lightbox uses the MIME
 * to pick a renderer (image / video / pdf / fallback). We don't fetch the
 * server's mime_types view because the few cases here are stable and a
 * round-trip would just delay opening the viewer.
 */
function inferContentType(extension: string | null | undefined): string {
  if (!extension) return 'application/octet-stream';
  const ext = extension.replace(/^\./, '').toLowerCase();
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'svg':
      return 'image/svg+xml';
    case 'mp4':
      return 'video/mp4';
    case 'webm':
      return 'video/webm';
    default:
      return 'application/octet-stream';
  }
}

function FileDetailsBody({
  file,
  onPreviewClick,
}: {
  file: CatalogContentFile;
  onPreviewClick: () => void;
}) {
  const hasFile = file.file_recnum != null;
  return (
    <div className="space-y-4 p-4">
      <PreviewSection
        fileId={hasFile ? file.recnum : 0}
        checksum={file.checksum ?? null}
        extension={file.file_extension ?? null}
        onClick={onPreviewClick}
      />

      {file.revision_message?.trim() && (
        <section>
          <h3 className="text-muted-foreground text-xs font-medium uppercase">
            Revision message
          </h3>
          <p className="text-sm">{file.revision_message}</p>
        </section>
      )}

      {!hasFile ? (
        <p className="text-muted-foreground text-sm italic">
          No revision uploaded yet.
        </p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Revision</dt>
          <dd>{revisionLabel(file.revision_nr ?? 0)}</dd>

          <dt className="text-muted-foreground">Date</dt>
          <dd>
            {file.date_in ? format(file.date_in, 'yyyy-MM-dd HH:mm') : '—'}
          </dd>

          <dt className="text-muted-foreground">Size</dt>
          <dd>
            {bytes(file.size ?? 0, { decimalPlaces: 1, unitSeparator: ' ' })}
          </dd>

          <dt className="text-muted-foreground">Extension</dt>
          <dd>{file.file_extension}</dd>

          <dt className="text-muted-foreground">Checksum</dt>
          <dd className="font-mono text-xs">{file.checksum}</dd>
        </dl>
      )}
    </div>
  );
}

function PreviewSection({
  fileId,
  checksum,
  extension,
  onClick,
}: {
  fileId: number;
  checksum: string | null;
  extension: string | null;
  onClick?: () => void;
}) {
  // Retry-once state: useFilePreview hook re-runs when `punch` flips.
  const [punch, setPunch] = useState(false);
  const preview = useFilePreview({
    fileId,
    checksum,
    extension,
    punch,
  });

  const isOpenable =
    !!onClick &&
    (preview.status === 'ready' || preview.status === 'unsupported');

  const handleClick = () => {
    if (isOpenable) onClick?.();
  };

  return (
    <section
      role={isOpenable ? 'button' : undefined}
      tabIndex={isOpenable ? 0 : undefined}
      aria-label={isOpenable ? 'Open viewer' : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isOpenable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        'bg-muted/30 flex min-h-[120px] items-center justify-center rounded p-2',
        isOpenable &&
          'group hover:bg-muted/50 cursor-pointer transition-colors',
      )}
    >
      {preview.status === 'ready' && (
        <img
          src={preview.src}
          alt="First page preview"
          className="max-h-[500px] max-w-full rounded shadow-sm group-hover:shadow-md"
        />
      )}
      {preview.status === 'busy' && (
        <div className="space-y-2 text-center">
          <Skeleton className="mx-auto h-32 w-24" />
          <p className="text-muted-foreground text-xs">{preview.task}</p>
        </div>
      )}
      {preview.status === 'unsupported' && (
        <p className="text-muted-foreground text-sm italic">
          {isOpenable
            ? 'No inline preview — tap to open in viewer.'
            : 'Preview not available for this file type.'}
        </p>
      )}
      {preview.status === 'failed' && (
        <div
          className="space-y-2 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-destructive text-sm">
            Preview failed: {preview.error}
          </p>
          {!punch && (
            <button
              type="button"
              onClick={() => setPunch(true)}
              className="text-primary text-xs underline"
            >
              Retry without cache
            </button>
          )}
        </div>
      )}
      {preview.status === 'idle' && null}
    </section>
  );
}

function FileDetailsSkeleton() {
  return (
    <div className="space-y-3 p-4">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-4 w-40" />
    </div>
  );
}
